import React from 'react';
import { useTranslation } from 'react-i18next';

import { ExternalLink as ExternalLinkContentfulModel } from 'src/api/contentful/contentTypes.cloverdotcom';
import { isChildrenDefined } from 'src/utils/ChildrenCheck';
import { isEntry } from 'src/utils/EntryUtil';
import { getUrlWithParamsOrPdpRedirect, shouldRedirectExistingMerchant } from 'src/utils/ExistingMerchant';

import { testAttribute } from '../../utils/Serenity';
import { applyEntryDataAttributes } from '../EntryWrapper/EntryDataWrapper';
import { getReferralType } from '../SalesChannel/Util';
import { useShopTemplateContext } from '../ShopTemplateContext/ShopTemplateContext';
import { TrackedLink } from '../TrackedLink/TrackedLink';
import { IContentLink } from './IContentLink';

export interface IExternalLinkProps extends IContentLink<ExternalLinkContentfulModel> {
  titleTag?: string;
  dataTestPrefix?: string;
}

const ExternalLink: React.FC<IExternalLinkProps> = ({
  entry,
  target,
  rel,
  children,
  hideTitle,
  innerRef,
  titleTag = 'span',
  dataTestPrefix,
  ...props
}) => {
  const shopTemplate = useShopTemplateContext();

  if (!isEntry(entry)) return null;
  // Aliasing hint and title as helpText and linkText as this is more descriptive of what function these variables serve
  const { hint: helpText, newTab } = entry.fields;
  let { title: linkText } = entry.fields;

  const { t } = useTranslation();

  let href = getUrlWithParamsOrPdpRedirect(entry);
  const shouldRedirectToPDP = shouldRedirectExistingMerchant(entry);

  const isPhoneNumber = (href: string | undefined) => href && href.includes('tel:');

  if (getReferralType() === 'BC' && isPhoneNumber(href)) return null;

  if (shopTemplate.reseller?.phoneNumberLink && isPhoneNumber(href)) {
    linkText = shopTemplate.reseller.phoneNumberLink.linkText;
    href = shopTemplate.reseller.phoneNumberLink.url;
  }
  const dataTest = dataTestPrefix ? `${dataTestPrefix}-${testAttribute(linkText)}` : `${testAttribute(linkText)}`;

  const childrenIsValid = isChildrenDefined(children);

  return (
    <TrackedLink
      ref={innerRef}
      href={href || undefined}
      target={target || (newTab && '_blank') || undefined}
      title={helpText || linkText}
      rel={[newTab && 'noopener', rel].filter(s => !!s).join(' ') || undefined}
      data-test={dataTest}
      {...props}
    >
      {(shouldRedirectToPDP && !childrenIsValid) && React.createElement(titleTag, { 'data-test': 'text' }, t('contact-sales'))}
      {!(shouldRedirectToPDP || hideTitle) && React.createElement(titleTag, '', linkText)}
      {children}
    </TrackedLink>
  );
};

const Wrapper = applyEntryDataAttributes(ExternalLink);
type Wrapper = typeof Wrapper;
export { Wrapper as ExternalLink };
